/**
 * Mails und Mail-Anhänge aus dem Postfach ziehen (User 01.11.2026: „Anhänge
 * Drag and Drop … z. B. in den Vorgang“, „Mail in einem Tab in Dokumente in
 * einem anderen Tab ziehen“).
 *
 * Quelle ist die Mailliste bzw. das Mailfenster, Ziel eine Karte im Vorgang
 * (`DateiDropZoneV4`) oder die Dokumente eines Kontakts (`DocumentsSection`).
 * Beide können in verschiedenen Browserfenstern liegen: Ein KMVS-Fenster
 * lässt sich auslagern (`windowManager/browserFenster`). Deshalb reist die
 * Nutzlast ausschließlich im `DataTransfer` — der Browser trägt ihn über die
 * Fenstergrenze, ein Modul-Zustand oder ein React-Context nicht zuverlässig
 * (ein Zug, der in einem anderen Browserfenster startet, landet sonst bei
 * einem Ziel, das von ihm nichts weiß).
 *
 * Eigene MIME-Typen, getrennt von `application/x-kmvs-mail` (Mail AUS dem
 * Vorgang): Die Vorgangskarten sollen eine Mail aus ihrer eigenen
 * Ereignisliste nicht ein zweites Mal verknüpfen, und das Mailfenster hängt
 * nur Mails mit Verknüpfung als `.eml` an. Reine Ableitungen, DOM-frei.
 */
import type { MailAttachment } from "../../services/graphMailService";
import { leseMailZug, type MailZug } from "../vorgang/detail/todoDrop";

export { ZIEHTYP_MAIL_ANHANG, ZIEHTYP_POSTFACH_MAIL } from "../vorgang/detail/todoDrop";

/** Eine Mail aus dem Postfach — derselbe Snapshot, aus dem ein MailLink entsteht. */
export type PostfachMailZug = Omit<MailZug, "linkId">;

/** Ein gezogener Anhang: genug, um ihn serverseitig über Graph zu holen. */
export interface AnhangZug {
    messageId: string;
    attachmentId: string;
    name: string;
    contentType?: string | null;
    size?: number | null;
}

/** Anhänge einer Mail (eines Postfachs) in einem Zug. */
export interface AnhaengeZug {
    mailbox: string;
    anhaenge: AnhangZug[];
}

export function postfachMailNutzlast(zug: PostfachMailZug): string {
    return JSON.stringify(zug);
}

/** Liest eine gezogene Postfach-Mail; Fremdes oder Kaputtes wird `null`. */
export function lesePostfachMailZug(roh: string | null | undefined): PostfachMailZug | null {
    const zug = leseMailZug(roh);
    if (!zug) return null;
    // Eine Verknüpfungs-Id gehört nicht zu diesem Zug — sie würde sonst
    // unbemerkt mitreisen.
    const { linkId: _ohne, ...rest } = zug;
    void _ohne;
    return rest;
}

export function anhaengeNutzlast(mailbox: string, anhaenge: readonly MailAttachment[]): string {
    const zug: AnhaengeZug = {
        mailbox,
        anhaenge: anhaenge.map((a) => ({
            messageId: a.messageId,
            attachmentId: a.id,
            name: a.name,
            contentType: a.contentType || null,
            size: Number.isFinite(a.size) ? a.size : null,
        })),
    };
    return JSON.stringify(zug);
}

/** Liest gezogene Anhänge; unbrauchbare Einträge fallen still heraus. */
export function leseAnhaengeZug(roh: string | null | undefined): AnhaengeZug | null {
    if (!roh) return null;
    let daten: unknown;
    try {
        daten = JSON.parse(roh);
    } catch {
        return null;
    }
    if (!daten || typeof daten !== "object" || Array.isArray(daten)) return null;
    const d = daten as Record<string, unknown>;
    const mailbox = typeof d.mailbox === "string" ? d.mailbox.trim() : "";
    if (!mailbox || !Array.isArray(d.anhaenge)) return null;
    const anhaenge: AnhangZug[] = [];
    for (const roh of d.anhaenge) {
        if (!roh || typeof roh !== "object") continue;
        const a = roh as Record<string, unknown>;
        const messageId = typeof a.messageId === "string" ? a.messageId.trim() : "";
        const attachmentId = typeof a.attachmentId === "string" ? a.attachmentId.trim() : "";
        if (!messageId || !attachmentId) continue;
        const size = typeof a.size === "number" && Number.isFinite(a.size) && a.size >= 0 ? a.size : null;
        anhaenge.push({
            messageId,
            attachmentId,
            name: typeof a.name === "string" && a.name.trim() ? a.name.trim() : "Anhang",
            contentType: typeof a.contentType === "string" && a.contentType ? a.contentType : null,
            size,
        });
    }
    return anhaenge.length > 0 ? { mailbox, anhaenge } : null;
}
